/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * CART-19 — Kit & Package storefront helpers.
 *
 * Mirrors the field contract emitted by the SCAPI kit hooks in
 * `cartridges/sfnext_ashley_hooks/cartridge/scripts/kit/`:
 *   - product GET   → KitEnrichmentHelper.enrichProductResponse
 *   - basket GET    → KitBasketEnrichmentHelper.enrichBasketResponse
 *   - add-to-cart   → KitExplosionHandler (explodes a virtual master into
 *                     individually-priced component PLIs)
 *
 * Every composition / pricing / shipping decision happens server-side; these
 * helpers only project the resolved `c_*` fields into UI-friendly shapes. As
 * with `mattress-protector-utils.ts`, keeping the field families as typed
 * interfaces (not raw record indexing) gives consumers autocomplete +
 * compile-time checks, and surfaces any cartridge/storefront contract drift as
 * a build error here.
 */

import type { ShopperBasketsV2 } from '@/scapi';
import { getDisplayedItemLineTotal } from '@/lib/cart/order-totals';

/** afmShippingOption enum values stamped on kit component line items. */
export const SHIP_ONLY_COMPLETE_ORDER = 'ShipOnlyCompleteOrder';
export const SHIP_ITEMS_AS_AVAILABLE = 'ShipItemsAsAvailable';

/**
 * A single resolved kit/package component, as serialized onto the product GET
 * response (`c_kitComponents[]`) by `KitEnrichmentHelper.toComponentDto`.
 */
export interface KitComponentDto {
    sku: string;
    name?: string | null;
    apkQuantity?: number;
    afmShippingOption?: string | null;
    deliveryMethod?: string | null;
    kitSku?: string | null;
    notAvailableForIndividualSale?: boolean;
}

/**
 * Kit/package fields attached to a PRODUCT GET response for a virtual master.
 * A non-kit product carries none of these.
 */
export interface KitProductFields {
    /** `true` on a kit/package master (driven by `kitFlag`). */
    c_isKit?: boolean;
    c_kitFlag?: boolean;
    /** `true` when the master is also a configurable package (`packageFlag`). */
    c_packageFlag?: boolean;
    /** Resolved component list (sum of these drives the roll-up price). */
    c_kitComponents?: KitComponentDto[];
    /**
     * Component SKUs that could not be resolved in the catalog. SCAPI omits the
     * field entirely when empty, so absent ≡ none missing.
     */
    c_kitMissingComponents?: string[];
    /**
     * Data-integrity orderability: `false` when the kit has missing components.
     * Add-to-cart is gated on this (a broken kit is never purchasable).
     */
    c_kitOrderable?: boolean;
    /** Roll-up selling price of the otherwise-priceless virtual master. */
    c_kitSellPrice?: number;
    /** Roll-up standard (strikethrough) price. */
    c_kitStandardPrice?: number;
    /** Whether the standard price should be shown as a strikethrough. */
    c_kitShowStandardPrice?: boolean;
    c_kitCurrencyCode?: string;
}

/**
 * Kit fields attached to each component PRODUCT ITEM in a BASKET GET response
 * (the master never persists as a line — only its exploded components do).
 */
export interface KitLineItemFields {
    /** Grouping key shared by every component of one kit instance. */
    c_partOfKitNumber?: string | null;
    /** Master product id, resolved from the grouping key. */
    c_kitMasterId?: string | null;
    /** Master display name — the kit card title. */
    c_kitDisplayName?: string | null;
    /** Per-kit multiplier for this component. */
    c_apkQuantity?: number;
    /** Component fulfillment grouping (ShipOnlyCompleteOrder | ShipItemsAsAvailable). */
    c_afmShippingOption?: string | null;
}

/**
 * One kit instance, as serialized at the BASKET level (`c_kitGroups[]`) by
 * `KitBasketEnrichmentHelper`.
 */
export interface KitGroup {
    /** Grouping key (master `itemNumber`, else master id). */
    kitNumber: string;
    masterId: string | null;
    /** Master display name (kit card title). */
    name: string | null;
    /** Component line-item ids (UUIDs) that belong to this kit instance. */
    itemIds: string[];
    /** Number of kit instances in the basket. */
    quantity: number;
    /** Roll-up line total = Σ component adjusted prices. */
    lineTotal: number;
}

/** Basket-level kit fields. */
export interface KitBasketFields {
    c_kitGroups?: KitGroup[];
}

/**
 * Whether a PRODUCT (PDP / product GET) is a kit or package master.
 * @param product - any product-shaped object that may carry the kit fields
 */
export function isKitProduct(product: KitProductFields | null | undefined): boolean {
    return Boolean(product?.c_isKit);
}

/** Whether the kit master is also a configurable package. */
export function isPackageProduct(product: KitProductFields | null | undefined): boolean {
    return Boolean(product?.c_packageFlag);
}

/** True when a kit master carries a usable roll-up selling price. */
export function hasKitRollupPrice(product: KitProductFields | null | undefined): boolean {
    return typeof product?.c_kitSellPrice === 'number' && Number.isFinite(product.c_kitSellPrice);
}

/**
 * Whether the kit can be added to cart. A kit with unresolved components
 * (`c_kitOrderable === false`) is never purchasable. The flag is only emitted
 * for kit masters, so a non-kit always returns `true` here.
 */
export function isKitOrderable(product: KitProductFields | null | undefined): boolean {
    if (!isKitProduct(product)) return true;
    return product?.c_kitOrderable !== false && hasKitRollupPrice(product);
}

/** Resolved component list for a kit master (empty array when absent). */
export function getKitComponents(product: KitProductFields | null | undefined): KitComponentDto[] {
    return product?.c_kitComponents ?? [];
}

/** Component SKUs the backend could not resolve (empty array when none). */
export function getKitMissingComponents(product: KitProductFields | null | undefined): string[] {
    return product?.c_kitMissingComponents ?? [];
}

/** Basket-level kit groups (empty array when the basket has no kits). */
export function getKitGroups(basket: KitBasketFields | null | undefined): KitGroup[] {
    return basket?.c_kitGroups ?? [];
}

/**
 * Item-shape needed to reconstruct kit groups purely from line-item metadata.
 * Matches the fields SCAPI auto-emits on every `ProductItem` when the kit
 * component PLI carries `custom.partOfKitNumber` (registered as a system
 * attribute on `ProductLineItem`, so SCAPI exposes it as `c_partOfKitNumber`
 * without needing a modifyGETResponse hook to rewrite the response).
 *
 * A component may also carry `c_kitMasterId` / `c_kitDisplayName` when the
 * server enricher (`KitBasketEnrichmentHelper`) has run, but the ONLY field
 * required for grouping is `c_partOfKitNumber`. Master metadata is looked up
 * against the fetched `productsById` map on the render side.
 */
export type KitReconstructableItem = {
    itemId?: string | null;
    productId?: string | null;
    productName?: string | null;
    price?: number | null;
    basePrice?: number | null;
    quantity?: number | null;
    priceAfterItemDiscount?: number | null;
    priceAdjustments?: ShopperBasketsV2.schemas['PriceAdjustment'][];
} & Partial<KitLineItemFields>;

/**
 * Reconstruct `KitGroup[]` client-side from a flat list of product items —
 * used as a fallback when the SCAPI response does NOT carry the enricher's
 * `c_kitGroups` payload (legacy orders placed before the enricher shipped;
 * sandboxes where the `sfnext_ashley_hooks` cartridge hasn't been deployed;
 * response paths that bypass `dw.ocapi.shop.order.modifyGETResponse`).
 *
 * Grouping key is `c_partOfKitNumber` on each item — the same key
 * `KitExplosionHandler` stamps at add-to-cart, and the same key
 * `KitBasketEnrichmentHelper.enrichBasketResponse` groups on server-side.
 * That key is the kit master's `custom.itemNumber` (else its product ID),
 * so we surface it as both `kitNumber` and `masterId` — the confirmation UI
 * uses `masterId` to look up the master product in `productsById` for the
 * hero image and title, exactly like the server-emitted groups.
 *
 * `lineTotal` is the sum of each component's line price (matches the
 * server's Σ `pli.getAdjustedPrice()` semantics — both include applied
 * item-level promotions). `quantity` is the min kit-instance count over
 * components, defaulting to 1 when we can't derive an apkQuantity.
 *
 * Returns an empty array when no item carries `c_partOfKitNumber` (non-kit
 * order — fast path, zero allocations).
 */
export function reconstructKitGroupsFromItems(items: KitReconstructableItem[]): KitGroup[] {
    // Accumulator carries an extra `minQty` so both `lineTotal` and the
    // kit-instance quantity can be derived in a single pass over `items`
    // (avoids the O(N × K) inner loop when a basket has many kits).
    type Accum = {
        kitNumber: string;
        masterId: string | null;
        name: string | null;
        itemIds: string[];
        quantity: number;
        lineTotal: number;
        minQty: number | null;
    };
    const groups = new Map<string, Accum>();

    for (const item of items) {
        const kitNumber = item.c_partOfKitNumber;
        if (!kitNumber || !item.itemId) continue;

        let group = groups.get(kitNumber);
        if (!group) {
            group = {
                kitNumber,
                // Prefer the server-emitted master id when present; fall back
                // to the grouping key itself (which is the master's
                // itemNumber || productID by construction — see
                // `KitExplosionHandler.kitKeyFor`).
                masterId: item.c_kitMasterId ?? kitNumber,
                name: item.c_kitDisplayName ?? null,
                itemIds: [],
                quantity: 0,
                lineTotal: 0,
                minQty: null,
            };
            groups.set(kitNumber, group);
        }

        group.itemIds.push(item.itemId);

        const qty = item.quantity ?? 1;
        // Prefer the after-item-discount line total so reconstructed kit
        // cards match the confirmation merchandise subtotal. Shopper Orders
        // often returns `price` as the unadjusted XML net-price; the helper
        // applies item promotions (and strips recycling) in that case.
        group.lineTotal += getDisplayedItemLineTotal(item);

        // Track minimum raw component quantity in the same pass. The server
        // groups derive kit-instance count as
        // `min(floor(componentQty / apkQuantity))` — we can't do that
        // client-side (apkQuantity lives on the PLI's `custom.*`, not on
        // the response schema), so this fallback approximates with
        // `min(componentQty)`. This is EXACT when every component has
        // `apkQuantity = 1` (the vast majority of Ashley kits), and
        // over-counts kit instances when a component has apkQuantity > 1.
        // Consumers that need exact instance counts (e.g. price roll-up)
        // must use the server groups (`getKitGroups`, not the fallback).
        if (group.minQty === null || qty < group.minQty) group.minQty = qty;
    }

    if (groups.size === 0) return [];

    // Finalize the KitGroup shape — drop the internal `minQty` accumulator
    // and clamp the quantity to at least 1.
    return Array.from(groups.values()).map(({ minQty, ...group }) => ({
        ...group,
        quantity: minQty && minQty > 0 ? minQty : 1,
    }));
}

/**
 * Get kit groups with client-side reconstruction as a fallback. Use this on
 * surfaces (order confirmation, order history) where the response is served
 * by a code path that MAY or MAY NOT run the server enricher — the
 * reconstructed groups match the server contract closely enough for
 * grouping / clustering, and the render side prefers server-emitted master
 * name/id when present.
 */
export function getKitGroupsWithFallback(
    basket: KitBasketFields | null | undefined,
    items: KitReconstructableItem[]
): KitGroup[] {
    const serverGroups = getKitGroups(basket);
    if (serverGroups.length > 0) return serverGroups;
    return reconstructKitGroupsFromItems(items);
}

/**
 * Build a lookup from a component line-item id → its owning kit group, so a
 * flat product-item list can be clustered into kit cards in a single pass.
 */
export function buildKitGroupByItemId(groups: KitGroup[]): Map<string, KitGroup> {
    const byItemId = new Map<string, KitGroup>();
    for (const group of groups) {
        for (const itemId of group.itemIds) {
            byItemId.set(itemId, group);
        }
    }
    return byItemId;
}

/**
 * Line-item shape needed by the kit-aware count helpers below. Kept minimal so
 * the helper can consume either the SCAPI `ProductItem` (used by the middleware
 * snapshot) or the storefront's `EnrichedProductItem` — both carry `itemId`,
 * `quantity`, and the `c_partOfKitNumber` kit-component marker.
 */
type KitCountableItem = {
    itemId?: string;
    quantity?: number;
} & Partial<KitLineItemFields>;

/**
 * Canonical shopper-facing kit quantity/include counts shared across cart,
 * checkout review, and order confirmation.
 */
export interface KitDisplaySummary {
    quantity: number;
    includesCount: number;
}

/**
 * Resolve display counts for a kit row.
 *
 * - `quantity`: prefers a derived kit-instance count from component quantities
 *   (`component.quantity / c_apkQuantity`) when available, else `group.quantity`.
 * - `includesCount`: number of component entries in one kit instance, anchored
 *   on `group.itemIds` for cross-surface consistency.
 */
export function getKitDisplaySummary(
    group: KitGroup,
    componentItems: Array<
        | {
              quantity?: number | null;
          }
        | (Partial<KitLineItemFields> & { quantity?: number | null })
    >
): KitDisplaySummary {
    const derivedCandidates = componentItems
        .map((item) => {
            if (typeof item.quantity !== 'number' || item.quantity <= 0) return undefined;
            const apkQuantity = (item as { c_apkQuantity?: number }).c_apkQuantity;
            if (typeof apkQuantity === 'number' && apkQuantity > 0) {
                return item.quantity / apkQuantity;
            }
            return item.quantity;
        })
        .filter((candidate): candidate is number => typeof candidate === 'number' && Number.isFinite(candidate));

    const derivedQuantity =
        derivedCandidates.length > 0 ? Math.max(1, Math.floor(Math.min(...derivedCandidates))) : undefined;
    const groupQuantity = typeof group.quantity === 'number' && group.quantity > 0 ? group.quantity : undefined;
    const quantity = derivedQuantity ?? groupQuantity ?? 1;

    const includesCount = group.itemIds.length > 0 ? group.itemIds.length : Math.max(1, componentItems.length);

    return { quantity, includesCount };
}

/**
 * SSF-390 Issues 1 & 8 — kit-aware basket item count.
 *
 * Backend explosion (`KitExplosionHandler.js`) turns one kit master into N
 * component PLIs where `quantity = apkQuantity × kit instances`. Summing raw
 * `productItems[].quantity` therefore inflates the count: a 4-piece kit at
 * qty 1 lands as 4, a 4-piece kit at qty 2 lands as 8, etc. To the shopper
 * the kit is a single product with a single quantity dial, so the badge, the
 * mini-cart title, the cart page title, and the cookie snapshot all need to
 * count the kit as `group.quantity` (kit instances), NOT the sum of its
 * component PLI quantities.
 *
 * Rule:
 *   count = Σ (non-kit merchandise item.quantity)
 *         + Σ (kitGroup.quantity)
 *
 * Non-kit items keep their normal per-PLI count; each kit instance counts as
 * one unit exactly once (via `kitGroups[]`). Kit-component PLIs contribute
 * zero because they are already represented by their owning group.
 */
export function getBasketItemCount(items: KitCountableItem[], kitGroups: KitGroup[]): number {
    let count = 0;
    for (const item of items) {
        // Skip kit components — every one of them is represented by exactly
        // one entry in `kitGroups[]` and would double-count otherwise.
        if (item.c_partOfKitNumber) continue;
        count += item.quantity ?? 0;
    }
    for (const group of kitGroups) {
        count += group.quantity ?? 0;
    }
    return count;
}

/**
 * Kit-aware unique-product count. A kit shows in the cart as a single card
 * regardless of how many components it explodes to, so it must contribute 1 to
 * "how many distinct products are in your basket" — not N (one per component).
 *
 *   uniqueProductCount = Σ (1 per non-kit merchandise PLI)
 *                      + Σ (1 per kit group)
 */
export function getBasketUniqueProductCount(items: KitCountableItem[], kitGroups: KitGroup[]): number {
    let unique = 0;
    for (const item of items) {
        if (item.c_partOfKitNumber) continue;
        unique += 1;
    }
    unique += kitGroups.length;
    return unique;
}

/**
 * Minimal price-shape needed to compute a kit's pre-promo roll-up. Matches the
 * fields SCAPI writes on every basket `ProductItem`:
 *
 *   - `basePrice` × `quantity` — the PRE-promo per-line total. `basePrice` is
 *     always the per-unit catalog/list price, unaffected by item-level
 *     promotions (see `getPriceData`'s basket-item branch in
 *     `product-price/utils.ts`), so this is the correct strikethrough source.
 *   - `price` — used as a fallback ONLY when `basePrice` is absent (e.g. a
 *     bundle component that inherits price from its parent). NOTE: `price` is
 *     the LINE total AFTER any item-level discount (`basePrice` × `quantity`
 *     when no discount applies, the discounted total otherwise) — it must
 *     never be preferred over `basePrice` here, or a genuinely discounted kit
 *     would resolve `originalLineTotal` to the same (already-discounted)
 *     value as `group.lineTotal` and silently suppress the strikethrough.
 *   - `priceAdjustments[]` — item-level applied promotions. Same shape as
 *     `<CartLinePromoCallouts>` expects.
 */
/**
 * Kit-level price adjustment. Aliased to the SCAPI `PriceAdjustment` schema so
 * the aggregated array can flow straight into `<CartLinePromoCallouts>` (which
 * types its `priceAdjustments` against the same schema).
 */
export type KitPriceAdjustment = ShopperBasketsV2.schemas['PriceAdjustment'];

export type KitComponentPricingItem = {
    itemId?: string;
    price?: number;
    basePrice?: number;
    quantity?: number;
    priceAfterItemDiscount?: number;
    priceAdjustments?: KitPriceAdjustment[];
    /**
     * Item-level promotion callouts from the basket PLI. SFCC often omits
     * `calloutMsg` on `priceAdjustments[]` while populating it here — same
     * enrichment source `<ProductItem>` uses so kit callouts are not a bare
     * `(-$X.XX)` while the Promotions API is still pending.
     */
    productPromotions?: Array<{ promotionId?: string; calloutMsg?: string }>;
};

/**
 * SSF-390 Issue 6 — aggregate kit-level pricing for the cart-line UI.
 *
 * The kit master persists as N exploded component PLIs; SCAPI applies each
 * price adjustment to every component (mirroring how the promo engine sees
 * every PLI). To show the "10% off" discount as one strikethrough + one
 * callout on the kit card (instead of N repeated callouts and no pre-promo
 * price at all), aggregate here:
 *
 *   - `originalLineTotal` — Σ pre-promo component line totals
 *     (`basePrice × quantity`, falling back to `price` only when `basePrice`
 *     is absent). This is the strikethrough source — see the note on
 *     `KitComponentPricingItem` above for why `basePrice` must win.
 *   - `savings`           — `originalLineTotal − group.lineTotal` (0 when no
 *     promo applies).
 *   - `promoAdjustments`  — one entry per unique `promotionId`, with its
 *     total `price` delta summed across components. Feeds
 *     `<CartLinePromoCallouts>` as-if it were a single-PLI adjustment list.
 *
 * Rationale for storefront aggregation (vs. extending
 * `KitBasketEnrichmentHelper`): the component PLIs already carry the raw
 * data, keeping the derivation client-side avoids a backend contract change
 * for a purely presentational roll-up. If a future ticket needs the pre-promo
 * total on the SCAPI response itself (e.g. for a downstream marketing
 * integration), promote this to `c_kitGroups[].originalLineTotal` and
 * `.promoCallouts[]` on the server.
 */
export interface KitGroupPricingSummary {
    originalLineTotal: number;
    lineTotal: number;
    savings: number;
    hasSavings: boolean;
    promoAdjustments: KitPriceAdjustment[];
}

export function getKitGroupPricing(
    group: KitGroup,
    componentItems: KitComponentPricingItem[],
    masterProduct?: KitProductFields | null
): KitGroupPricingSummary {
    let componentOriginalLineTotal = 0;
    let componentDiscountedLineTotal = 0;
    // Merge duplicate adjustments (same `promotionId` across components) into a
    // single row so the shopper sees "10% Off (-$25.00)" once, not "-$5" × 5.
    const byPromotion = new Map<string, KitPriceAdjustment>();

    for (const item of componentItems) {
        const qty = item.quantity ?? 1;
        // `basePrice` is the per-unit PRE-promo catalog price and must win here.
        // `price` is only a fallback for the rare case `basePrice` is absent
        // (e.g. an inherited bundle-component price) — `price` itself is the
        // LINE total AFTER any item-level discount, so preferring it would
        // silently collapse `originalLineTotal` down to `group.lineTotal`
        // (the post-promo total) and suppress the strikethrough on every
        // genuinely discounted kit.
        const perLine = typeof item.basePrice === 'number' ? item.basePrice * qty : (item.price ?? 0);
        componentOriginalLineTotal += perLine;
        componentDiscountedLineTotal += getDisplayedItemLineTotal(item);

        const promoCallouts: Record<string, string> = {};
        for (const pp of item.productPromotions ?? []) {
            if (pp.promotionId && pp.calloutMsg) {
                promoCallouts[pp.promotionId] = pp.calloutMsg;
            }
        }

        for (const adj of item.priceAdjustments ?? []) {
            // Group by promotionId when present, else fall back to the merchant-
            // authored itemText so a promotion missing an id still coalesces.
            const key = adj.promotionId || adj.itemText || adj.priceAdjustmentId || '';
            if (!key) continue;
            // Same contract as ProductItem.enrichedPriceAdjustments: copy
            // calloutMsg from productPromotions when the adjustment itself
            // doesn't carry one, so the Suspense fallback still has a label.
            const calloutMsg = adj.calloutMsg || (adj.promotionId ? promoCallouts[adj.promotionId] : undefined);
            const enrichedAdj = calloutMsg && calloutMsg !== adj.calloutMsg ? { ...adj, calloutMsg } : adj;
            const existing = byPromotion.get(key);
            if (existing) {
                existing.price = (existing.price ?? 0) + (adj.price ?? 0);
                if (!existing.calloutMsg && enrichedAdj.calloutMsg) {
                    existing.calloutMsg = enrichedAdj.calloutMsg;
                }
                if (!existing.itemText && enrichedAdj.itemText) {
                    existing.itemText = enrichedAdj.itemText;
                }
            } else {
                byPromotion.set(key, { ...enrichedAdj });
            }
        }
    }

    // The component-level total above only catches ITEM-LEVEL promotions
    // (`priceAdjustments` on the exploded PLIs). Many Ashley kits are marked
    // down purely via pricebook structure — a "sale" pricebook assigned above
    // the parent "list" pricebook — with NO `dw.campaign` promotion involved
    // at all. In that case every exploded component's `basePrice` already
    // equals its (sale-tier) `price` — there is no per-item discount to
    // detect, and `componentOriginalLineTotal` above silently equals
    // `group.lineTotal`.
    //
    // The kit MASTER carries the authoritative pre-markdown total via
    // `c_kitStandardPrice` (walks parent pricebooks — see
    // `KitPriceHelper.getKitRollupPrice` / `getComponentStandardPrice`),
    // the exact same field the PDP roll-up price uses (`getPriceData`'s kit
    // branch in `product-price/utils.ts`). Without folding it in here, a kit
    // that clearly shows "was $X" on its PDP loses that strikethrough the
    // moment it lands in the cart/checkout — see SSF-390 kit pricing bug.
    // `c_kitStandardPrice` is PER kit instance, so scale by `group.quantity`
    // to match `componentOriginalLineTotal` (which already sums every
    // instance's components).
    let masterOriginalLineTotal = 0;
    if (
        masterProduct?.c_kitShowStandardPrice === true &&
        typeof masterProduct.c_kitStandardPrice === 'number' &&
        Number.isFinite(masterProduct.c_kitStandardPrice)
    ) {
        masterOriginalLineTotal = masterProduct.c_kitStandardPrice * (group.quantity || 1);
    }

    // Take whichever candidate reports the larger pre-promo total so neither
    // an item-level promotion nor a pricebook markdown is ever under-reported.
    const originalLineTotal = Math.max(componentOriginalLineTotal, masterOriginalLineTotal);

    // Shopper Orders often stamps unadjusted `price` (XML net-price) on
    // components while `c_kitGroups[].lineTotal` / reconstructed totals
    // follow that same unadjusted figure. Prefer the lower of the group
    // total and the component after-item-discount sum so the kit card
    // current price matches the confirmation merchandise subtotal.
    const lineTotal =
        componentDiscountedLineTotal > 0 && group.lineTotal > 0
            ? Math.min(group.lineTotal, componentDiscountedLineTotal)
            : componentDiscountedLineTotal > 0
              ? componentDiscountedLineTotal
              : group.lineTotal;
    const savings = Math.max(0, originalLineTotal - lineTotal);
    // Guard against float drift — treat sub-cent deltas as "no promo".
    const hasSavings = savings > 0.005;

    return {
        originalLineTotal,
        lineTotal,
        savings,
        hasSavings,
        promoAdjustments: Array.from(byPromotion.values()),
    };
}
